import { TEAM_SORT_OPTIONS, TeamsList } from "../components/TeamsList";
import { DS, StatGroup, FormIntro, Glyph } from "../ds/Kit";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { useCallback, useState } from "react";
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals";
import { FormParser } from "src/view/FormParser";
import { GD } from "src/GD";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import { Typo } from "@ui/text/Typo";
import { toast } from "react-toastify";
import { Page } from "@ui/page/Page";
import { USER_ROLE } from "src/services/AuthService";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { DEFAULT_CURRENCY } from "src/constants";
import { getAverageBuyerBalancePerTeam } from "src/utils/buyerBalances";
import { DEFAULT_SORT, sortSelectValues } from "src/utils/listSort";
import { useIssuer } from "../issuer/IssuerProvider";


export const TeamsPage = () => {
  const { selectedIssuer } = useIssuer();
  const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY;
  const [filter, setFilter] = useState({ name: "", sort: "" });
  const [profile] = GD.REQ_PROFILE.useRequest();
  // Totals of every team matching the search, reported by the list.
  const [summary, setSummary] = useState<ITeamListSummary | null>(null);
  const money = (value: number) => formatCurrencyWithNumberToSymbolWithNumber(value, issuerCurrency);

  const handleCreateTeam = useCallback(() => {
    S_UNIVERSAL_DIALOG.invoke({
      label: "Create Team",
      content: (
        <>
          <FormIntro icon={Glyph.team} title="Create a team" description="Group buyers under a team lead to track their cards and spending together." />
          <FormParser
            onSubmit={async (data) => {
              const res = await GD.REQ_TEAM_CREATE.request({ name: data.name });
              if (res.error) return res;
              toast.success("Team created successfully");
              S_UNIVERSAL_DIALOG.invoke(null);
              return {};
            }}
            onRequest={async () => {
              return {
                name: "",
                $__decorator: {
                  title: "Create New Team",
                },
              };
            }}
            primaryButton={{ title: "Create" }}
            secondaryButton={{
              title: "Cancel",
              onClick: () => S_UNIVERSAL_DIALOG.invoke(null),
            }}
          />
        </>
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  }, []);

  return (
    <Page
      sx={{ boxShadow: "none" }}
      label="Teams"
      description="Manage members and track spending across your teams."
      sectionRightContent={
        profile?.company_role === USER_ROLE.ADMIN && (
          <Button
            onClick={handleCreateTeam}
            title="Create Team"
            variant="primarySmall"
          />
        )
      }
    >
      <VBox
        gap="24px"
        sx={{
          height: "100%",
          overflow: "hidden",
          "@media (max-width: 768px)": {
            height: "auto",
            overflow: "visible",
            gap: "16px",
          },
        }}
      >
        <StatGroup
          items={[
            { label: "Teams", value: summary ? summary.teams : "—", hint: summary ? `${summary.active} active` : undefined },
            { label: "Buyers", value: summary ? summary.buyers : "—" },
            { label: "Total buyer balance", value: summary ? money(summary.balance) : "—" },
            { label: "Avg. balance per team", value: summary ? money(getAverageBuyerBalancePerTeam(summary.balance, summary.teams)) : "—" },
          ]}
        />

        <HBox
          gap="12px"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            "@media (max-width: 600px)": { flexDirection: "column", alignItems: "stretch" },
          }}
        >
          <Typo sx={{ fontSize: "16px", fontWeight: 650, color: DS.ink }}>
            {summary ? `All teams · ${summary.teams}` : "All teams"}
          </Typo>
          <HBox gap="8px" sx={{ "@media (max-width: 600px)": { flexDirection: "column" } }}>
          <Box sx={{ width: "200px", "@media (max-width: 600px)": { width: "100%" } }}>
            <Select
              ariaLabel="Sort teams"
              data={{ sort: filter.sort || DEFAULT_SORT }}
              name="sort"
              values={sortSelectValues(TEAM_SORT_OPTIONS)}
              onChange={(val) => setFilter({ ...filter, sort: val === DEFAULT_SORT ? "" : String(val) })}
            />
          </Box>
          <Box sx={{ width: "300px", "@media (max-width: 600px)": { width: "100%" } }}>
            <Input
              // No label here; drop the gap Input keeps above the field for one so it lines up with the sort select.
              sx={{ gap: 0 }}
              placeholder="Search teams by name..."
              data={filter}
              name="name"
              onChange={(val) => setFilter({ ...filter, name: val })}
            />
          </Box>
          </HBox>
        </HBox>

        <Box sx={{ flexGrow: 1, minHeight: 0, margin: "-16px", "@media (max-width: 768px)": { margin: "-12px" } }}>
          <TeamsList filter={filter} onSummary={setSummary} />
        </Box>
      </VBox>
    </Page>
  );
};
